import { SerializedError } from "@reduxjs/toolkit";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";

import { requestFailureReason } from "./describeSaveFailure";
import { ERROR_TYPE, getErrorMessage, translateError } from "./errors";

/** A bare error code such as `SOMETHING_WRONG`, which is never shown to a user as it is. */
const ERROR_CODE = /^[A-Z][A-Z0-9_]*$/;

const statusOf = (error: unknown): number | undefined => {
	const status = (error as { status?: unknown } | null)?.status;

	return typeof status === "number" ? status : undefined;
};

/**
 * The sentence for a refused request (PREC-4634/4636, QA comment 27620): the translation when the
 * API answered with a code the web knows; otherwise, for a 4xx refusal, the API's own sentence
 * (the role-grant refusals explain themselves in plain words, e.g. "Only Signalist staff can grant
 * or remove the Admin role."); otherwise "Something went wrong". Null when there is no error.
 */
export const refusalText = (error: unknown): string | null => {
	if (!error) {
		return null;
	}

	const code = getErrorMessage(error as FetchBaseQueryError | SerializedError);

	if (code !== ERROR_TYPE.SOMETHING_WRONG && translateError[code]) {
		return translateError[code];
	}

	const status = statusOf(error);
	const reason = requestFailureReason(error);
	const isRefusal = status !== undefined && status >= 400 && status < 500;

	if (isRefusal && reason && !ERROR_CODE.test(reason)) {
		return reason;
	}

	return translateError[ERROR_TYPE.SOMETHING_WRONG];
};
